import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProvisionedApplicationsTypes } from '@app/Enums';
import { ToastMessageType, ToastService } from '@app/Common';
import { CreateTenantTrial } from '@app/Models/create-tenant-trial.model';
import { Tenant } from '@app/Models/tenant';
import { TenantGroup } from '@app/Models/tenant-group.model';
import { TenantService, TenantFormOptionsService } from '@app/Services';
import { TenantAlreadyExistsError } from '@app/Tenant/Validators/exceptions/tenant-already-exists-error.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatFormField, MatLabel, MatInput, MatError } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'app-new-tenant-trial-form',
    templateUrl: './new-tenant-trial-form.component.html',
    styleUrls: ['./new-tenant-trial-form.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatSelect, MatOption, MatButton]
})
export class NewTenantTrialFormComponent implements OnInit {
    @Output() formSubmitted = new EventEmitter<CreateTenantTrial>();
    @Input() isSubmitted: boolean;
    trialForm = new UntypedFormGroup({
        //name: new FormControl(null, [Validators.required]),
        company: new UntypedFormControl(null, [Validators.required]),
        //phone: new FormControl(null, [Validators.required]),
        //email: new FormControl(null, [Validators.required, Validators.email]),
        tenantIndustry: new UntypedFormControl(null, [Validators.required]),
        companySize: new UntypedFormControl(null, [Validators.required]),
        tenantGroup: new UntypedFormControl(null, [Validators.required]),
        bestPracticeTenant: new UntypedFormControl(null, [Validators.required]),
    });
    tenantIndustryTypes: string[] = [];
    tenantSizes: string[] = [];
    tenantPermissionGroups: TenantGroup[] = [];
    bestPracticeTenants: Tenant[] = [];

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly tenantService: TenantService,
        private readonly tenantFormOptionsService: TenantFormOptionsService,
        private readonly toast: ToastService,
    ) { }

    ngOnInit(): void {
        this.getTenantAttributes();
        this.tenantPermissionGroups = this.tenantFormOptionsService.getTenantPermissionGroups();
        this.getBestPracticeTenants();
    }

    submitForm(): void {
        const tenantTrialObject = this.createTenantTrialObject();

        this.tenantService.checkTenantExists(tenantTrialObject.Slug)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (doesExist) => {
                    if (doesExist) {
                        this.trialForm.get('company').setErrors(new TenantAlreadyExistsError());
                    } else {
                        this.formSubmitted.emit(tenantTrialObject);
                    }
                }
            });
    }

    isSubmitButtonAvailable(): boolean {
        return !this.isSubmitted && this.trialForm?.valid;
    }

    private createTenantTrialObject(): CreateTenantTrial {
      return {
        //Name: this.trialForm.get('name').value,
        Company: this.trialForm.get('company').value,
        Slug: this.trialForm.get('company').value.replace(/[^0-9a-z]/gi, ''),
        //PhoneNumber: this.trialForm.get('phone').value,
        //Email: this.trialForm.get('email').value,
        TenantIndustry: this.trialForm.get('tenantIndustry').value,
        CompanySize: this.trialForm.get('companySize').value,
        TenantPermissionGroup: this.trialForm.get('tenantGroup').value,
        BestPracticeTenant: this.trialForm.get('bestPracticeTenant').value,
      } as CreateTenantTrial;
    }

    tenantSlugAlreadyExists(): boolean {
        return this.trialForm.get('company').hasError('tenantAlreadyExists');
      }

    private getTenantAttributes(): void {
        this.tenantFormOptionsService.getTenantAttributes()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ tenantIndustryTypes, tenantSizes }) => {
                    this.tenantIndustryTypes = tenantIndustryTypes;
                    this.tenantSizes = tenantSizes;
                },
                error: (err) => {
                    this.toast.sendMessage(
                        'Unable to get Tenant Attributes',
                        err,
                        ToastMessageType.error);
                }
            });
    }

    private getBestPracticeTenants(): void {
        this.bestPracticeTenants = this.tenantService.tenants
            .filter((tenant) => tenant.HasAccess && 
                tenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb) &&
                tenant.BestPracticeTenant);            
    }
}
